/**
 * One official's circular avatar — the face where there is one, the initials where there is
 * not (run 29).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * WHY THIS IS ONE COMPONENT AND NOT TWO SPANS
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * Before run 29 the roster listing drew an initials circle and the profile card drew a
 * photograph, and the two knew nothing about each other. The Technical Delegate asked for the
 * face on the listing as well, which means every avatar on every screen now has the same
 * decision to make — photograph, or initials, at the size this site draws them — and a decision
 * made in three places is a decision that will be made differently in three places.
 *
 * **The fallback is silent and total.** No photograph, a photograph still in flight, a fetch
 * refused by the gate, a stored file that is not there: all four draw the initials the roster
 * drew before photographs existed. A broken-image glyph would read as a fault in the person's
 * record rather than in the fetch, and an empty circle would read as a record with a hole in
 * it. See `useOfficialPhoto` for the states and for the session cache that stops forty-eight
 * rows fetching forty-eight times.
 *
 * **`photoRef === null` fetches nothing at all.** The row already knows there is no photograph,
 * and a request that would certainly 404 is one not worth making on a venue connection.
 *
 * **It stays `aria-hidden`.** The name is in the very next column on the roster and beside the
 * circle everywhere else; a screen reader does not need the person twice, and "photograph of"
 * is not information a reader who has just heard the name is missing.
 */

import type { Gender } from "@hockeytd/shared";

import { initialsOf } from "./initials.js";
import { useOfficialPhoto } from "./useOfficialPhoto.js";

export interface OfficialAvatarProps {
  tmsPersonId: string;
  /** The name the initials come from, and the circle's hover title. */
  mediaName: string;
  /** Tints the initials circle. Null is NOT STATED and takes no tint at all. */
  gender: Gender | null;
  /** The stored photograph's file name. Null means there is none, and nothing is fetched. */
  photoRef: string | null;
  /**
   * `lg` is the roster LISTING's enlarged circle — the size the TD asked for, so that a face
   * reads at a glance down a column of forty-eight. Everywhere else keeps the inline size the
   * sheet has always drawn, which is why this is a modifier and not a change to `.oavatar`:
   * the appointments picker's dropdown shares that class and must not grow.
   *
   * **`xl` is RUN 31's doubling, and it is a THIRD size rather than a bigger `lg`.** The TD
   * asked for the pictures on The Tournament Roster to be twice the size; `lg` is rendered by
   * the same `RosterPanel` on the Reporting page, whose §A4 table he did not ask to change, so
   * growing `lg` in place would have moved a screen nobody complained about. The roster page
   * asks for `xl` explicitly and the Reporting page keeps `lg`.
   */
  size?: "sm" | "lg" | "xl";
}

export function OfficialAvatar({
  tmsPersonId,
  mediaName,
  gender,
  photoRef,
  size = "sm",
}: OfficialAvatarProps): React.JSX.Element {
  const photo = useOfficialPhoto(tmsPersonId, photoRef);

  const classes = [
    "oavatar",
    gender === null ? null : `oavatar--${gender}`,
    size === "sm" ? null : `oavatar--${size}`,
    photo.url === null ? null : "oavatar--photo",
  ]
    .filter((entry) => entry !== null)
    .join(" ");

  return (
    <span className={classes} aria-hidden="true" title={mediaName} data-testid={`avatar-${tmsPersonId}`}>
      {photo.url === null ? (
        initialsOf(mediaName)
      ) : (
        // `alt=""` because the wrapper is already hidden from assistive technology; the sizing
        // and the CROP live in `.oavatar__img`, never in an inline style, so that no render
        // site can accidentally stretch a photograph that was not sent square.
        <img className="oavatar__img" src={photo.url} alt="" data-testid={`avatar-photo-${tmsPersonId}`} />
      )}
    </span>
  );
}
